// src/components/RitualScreen.js
// Full-screen closing ritual shown after journaling.
// Day 1: shows only the day-specific ritual.
// Day 2+: shows the day-specific ritual, then the Magic Rock ritual.
import React, { useState } from 'react'
import { DAYS } from '../data/content'

const PlaceholderIllustration = ({ color, type, city }) => {
  if (type === 'magic-rock') {
    // Basquiat style: dark background, red faceted gemstone with cross-hatch, yellow crown, scrawled text
    return (
      <svg viewBox="0 0 320 240" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', height: '100%' }}>
        <defs>
          <clipPath id="gemClip">
            <polygon points="160,78 198,106 204,136 176,172 144,172 116,136 122,106"/>
          </clipPath>
        </defs>
        <rect width="320" height="240" fill="#111111"/>
        {/* Yellow 3-peaked crown */}
        <path d="M118,82 L118,56 L138,70 L160,44 L182,70 L202,56 L202,82 Z"
              stroke="#fccc0a" strokeWidth="3.5" fill="none" strokeLinejoin="miter"/>
        <circle cx="138" cy="59" r="4" fill="#fccc0a"/>
        <circle cx="160" cy="47" r="4" fill="#fccc0a"/>
        <circle cx="182" cy="59" r="4" fill="#fccc0a"/>
        {/* Red faceted gemstone */}
        <polygon points="160,78 198,106 204,136 176,172 144,172 116,136 122,106" fill="#cc1a18"/>
        {/* Cross-hatch fill inside gem */}
        <g clipPath="url(#gemClip)" stroke="#880000" strokeWidth="1.3" opacity="0.7">
          {[...Array(16)].map((_, i) => <line key={`ga${i}`} x1={22+i*12} y1={78} x2={22+i*12+94} y2={172}/>)}
          {[...Array(16)].map((_, i) => <line key={`gb${i}`} x1={22+i*12+94} y1={78} x2={22+i*12} y2={172}/>)}
        </g>
        {/* Gem facet lines */}
        <polygon points="160,78 198,106 204,136 176,172 144,172 116,136 122,106" fill="none" stroke="#000000" strokeWidth="2.5"/>
        <line x1="160" y1="78" x2="160" y2="172" stroke="#000" strokeWidth="1.8"/>
        <line x1="116" y1="136" x2="204" y2="136" stroke="#000" strokeWidth="1.8"/>
        <line x1="122" y1="106" x2="160" y2="78" stroke="#000" strokeWidth="1.2"/>
        <line x1="198" y1="106" x2="160" y2="78" stroke="#000" strokeWidth="1.2"/>
        <line x1="116" y1="136" x2="144" y2="172" stroke="#000" strokeWidth="1.2"/>
        <line x1="204" y1="136" x2="176" y2="172" stroke="#000" strokeWidth="1.2"/>
        {/* Scrawled text fragments */}
        <text x="42" y="92" fill="white" fontSize="8" fontFamily="monospace" opacity="0.85" transform="rotate(-14,42,92)">SAMO©</text>
        <text x="222" y="86" fill="#fccc0a" fontSize="7" fontFamily="monospace" opacity="0.9">©</text>
        <text x="220" y="96" fill="#fccc0a" fontSize="6" fontFamily="monospace" opacity="0.8">1982</text>
        <text x="48" y="158" fill="white" fontSize="7" fontFamily="monospace" opacity="0.7" transform="rotate(9,48,158)">MAGIC</text>
        <text x="218" y="165" fill="white" fontSize="6.5" fontFamily="monospace" opacity="0.75">ROCK</text>
        <text x="225" y="118" fill="#fccc0a" fontSize="13" fontFamily="monospace">★</text>
        <text x="36" y="120" fill="#aa1010" fontSize="14" fontFamily="monospace">✶</text>
        <text x="244" y="150" fill="white" fontSize="5.5" fontFamily="monospace" transform="rotate(-6,244,150)">UNTITLED</text>
        <text x="56" y="196" fill={color} fontSize="7" fontFamily="monospace">© {city.toUpperCase()}</text>
      </svg>
    )
  }

  if (type === 'city' && city === 'paris') {
    // Moebius style: cream background, four precise shoe prints with cross-hatch, alternating L/R, decreasing toward top-right
    return (
      <svg viewBox="0 0 320 240" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', height: '100%' }}>
        <defs>
          <clipPath id="fp1"><ellipse cx="82" cy="188" rx="20" ry="36" transform="rotate(-10,82,188)"/></clipPath>
          <clipPath id="fp2"><ellipse cx="140" cy="155" rx="15" ry="28" transform="rotate(10,140,155)"/></clipPath>
          <clipPath id="fp3"><ellipse cx="192" cy="127" rx="11" ry="21" transform="rotate(-10,192,127)"/></clipPath>
          <clipPath id="fp4"><ellipse cx="235" cy="104" rx="8" ry="16" transform="rotate(10,235,104)"/></clipPath>
        </defs>
        <rect width="320" height="240" fill="#f4efe5"/>
        {/* Footprint 1 — right foot, largest, bottom-left */}
        <ellipse cx="82" cy="188" rx="20" ry="36" transform="rotate(-10,82,188)" fill="white"/>
        <g clipPath="url(#fp1)" stroke="#2a2a2a" strokeWidth="1.0">
          {[...Array(15)].map((_, i) => <line key={`fa${i}`} x1={-10+i*8} y1={152} x2={-10+i*8+72} y2={224}/>)}
          {[...Array(15)].map((_, i) => <line key={`fb${i}`} x1={-10+i*8+72} y1={152} x2={-10+i*8} y2={224}/>)}
        </g>
        <ellipse cx="82" cy="188" rx="20" ry="36" transform="rotate(-10,82,188)" fill="none" stroke="#1a1a1a" strokeWidth="1.5"/>
        {/* Footprint 2 — left foot */}
        <ellipse cx="140" cy="155" rx="15" ry="28" transform="rotate(10,140,155)" fill="white"/>
        <g clipPath="url(#fp2)" stroke="#2a2a2a" strokeWidth="0.9">
          {[...Array(13)].map((_, i) => <line key={`fc${i}`} x1={69+i*8} y1={127} x2={69+i*8+56} y2={183}/>)}
          {[...Array(13)].map((_, i) => <line key={`fd${i}`} x1={69+i*8+56} y1={127} x2={69+i*8} y2={183}/>)}
        </g>
        <ellipse cx="140" cy="155" rx="15" ry="28" transform="rotate(10,140,155)" fill="none" stroke="#1a1a1a" strokeWidth="1.4"/>
        {/* Footprint 3 — right foot, medium */}
        <ellipse cx="192" cy="127" rx="11" ry="21" transform="rotate(-10,192,127)" fill="white"/>
        <g clipPath="url(#fp3)" stroke="#2a2a2a" strokeWidth="0.8">
          {[...Array(13)].map((_, i) => <line key={`fe${i}`} x1={139+i*7} y1={106} x2={139+i*7+42} y2={148}/>)}
          {[...Array(13)].map((_, i) => <line key={`ff${i}`} x1={139+i*7+42} y1={106} x2={139+i*7} y2={148}/>)}
        </g>
        <ellipse cx="192" cy="127" rx="11" ry="21" transform="rotate(-10,192,127)" fill="none" stroke="#1a1a1a" strokeWidth="1.3"/>
        {/* Footprint 4 — left foot, smallest, top-right */}
        <ellipse cx="235" cy="104" rx="8" ry="16" transform="rotate(10,235,104)" fill="white"/>
        <g clipPath="url(#fp4)" stroke="#2a2a2a" strokeWidth="0.7">
          {[...Array(13)].map((_, i) => <line key={`fg${i}`} x1={195+i*6} y1={88} x2={195+i*6+32} y2={120}/>)}
          {[...Array(13)].map((_, i) => <line key={`fh${i}`} x1={195+i*6+32} y1={88} x2={195+i*6} y2={120}/>)}
        </g>
        <ellipse cx="235" cy="104" rx="8" ry="16" transform="rotate(10,235,104)" fill="none" stroke="#1a1a1a" strokeWidth="1.2"/>
      </svg>
    )
  }

  if (type === 'city' && city === 'london') {
    // Banksy style: cream brick wall, bold stencil mirror frame, silhouette figure inside glass
    return (
      <svg viewBox="0 0 320 240" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', height: '100%' }}>
        <defs>
          <clipPath id="mirrorGlass">
            <ellipse cx="160" cy="118" rx="50" ry="67"/>
          </clipPath>
        </defs>
        <rect width="320" height="240" fill="#e8e0d2"/>
        {/* Brick wall */}
        {[...Array(15)].map((_, row) => {
          const y = row * 16
          const offset = row % 2 === 0 ? 0 : -20
          return [...Array(9)].map((_, col) => (
            <rect key={`b${row}-${col}`} x={offset + col * 40 + 1} y={y + 1} width={37} height={13} fill="#d0c4b0" rx="1"/>
          ))
        })}
        {/* Mirror frame outer (dark oval) */}
        <ellipse cx="160" cy="118" rx="65" ry="82" fill="#1a1a1a"/>
        {/* Mirror glass */}
        <ellipse cx="160" cy="118" rx="50" ry="67" fill="#b8b0a5"/>
        {/* Figure silhouette inside glass */}
        <g clipPath="url(#mirrorGlass)" fill="#111111">
          <circle cx="160" cy="64" r="10"/>
          <rect x="147" y="74" width="26" height="32" rx="3"/>
          <rect x="134" y="76" width="13" height="24" rx="3"/>
          <rect x="173" y="76" width="13" height="24" rx="3"/>
          <rect x="149" y="106" width="22" height="12" rx="2"/>
          <rect x="150" y="118" width="9" height="36" rx="2"/>
          <rect x="161" y="118" width="9" height="36" rx="2"/>
        </g>
        {/* Glass highlight */}
        <ellipse cx="152" cy="90" rx="14" ry="22" fill="white" opacity="0.1"/>
      </svg>
    )
  }

  // Default city-scene illustration
  return (
    <svg viewBox="0 0 320 240" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', height: '100%' }}>
      <defs>
        <linearGradient id="skyGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#0a0a1a" />
          <stop offset="100%" stopColor="#1a1040" />
        </linearGradient>
        <linearGradient id="cityGlow" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <rect width="320" height="240" fill="url(#skyGrad)" />
      {[[30,20],[70,35],[110,15],[160,30],[200,18],[245,40],[285,22],[50,60],[135,55],[220,65],[300,50]].map(([x,y],i) => (
        <circle key={i} cx={x} cy={y} r={1} fill="#fff" opacity={0.5 + (i % 4) * 0.12} />
      ))}
      <rect x="0" y="120" width="320" height="120" fill="url(#cityGlow)" />
      <rect x="0" y="160" width="320" height="80" fill="#0f0f0f" />
      <rect x="5"   y="100" width="35" height="60" fill="#141428" />
      <rect x="12"  y="88"  width="8"  height="14" fill="#141428" />
      <rect x="48"  y="120" width="28" height="40" fill="#1a1835" />
      <rect x="83"  y="95"  width="45" height="65" fill="#141428" />
      <rect x="90"  y="80"  width="6"  height="16" fill="#141428" />
      <rect x="135" y="115" width="32" height="45" fill="#1a1835" />
      <rect x="175" y="90"  width="42" height="70" fill="#141428" />
      <rect x="183" y="78"  width="7"  height="13" fill="#141428" />
      <rect x="225" y="108" width="36" height="52" fill="#1a1835" />
      <rect x="268" y="98"  width="30" height="62" fill="#141428" />
      <rect x="275" y="86"  width="6"  height="14" fill="#141428" />
      {[
        [10,108],[20,108],[10,120],[20,120],
        [88,102],[100,102],[88,115],[100,115],
        [178,98],[190,98],[178,111],[190,111],
        [230,116],[242,116],[230,128],[242,128],
        [272,106],[282,106],[272,118],[282,118],
      ].map(([x,y],i) => (
        <rect key={i} x={x} y={y} width={5} height={4} fill={color} opacity={0.4 + (i % 5) * 0.08} />
      ))}
      <rect x="0" y="232" width="320" height="8" fill={color} opacity="0.6" />
    </svg>
  )
}

// Ritual content for the Magic Rock
// Day 2: full intro (finding the rock, the explanation)
// Days 3-28: short nightly reminder only
const getMagicRockRitual = (city, day) => {
  if (day === 2) {
    return DAYS?.[city]?.[1]
  }
  const reminders = [
    'Wherever your rock is — find it. Hold it in your hand, think of the single best thing that happened today, and feel genuinely grateful for it. That\'s all. That\'s the magic.',
    'Before you sleep tonight, pick up your rock. One moment. One good thing from today. Hold it, feel it, mean it.',
    'Your rock is waiting. Pick it up, close your hand around it, and ask yourself: what was the best part of today? Stay with that feeling until it\'s real.',
    'Don\'t have your rock nearby? Use a coin, a stone from outside, anything solid. The object isn\'t magic — your gratitude is. Hold it, think of today\'s best moment, feel it fully.',
    'Quick check: where\'s your rock? Go get it. Hold it. Think of one good thing from today — just one — and be completely grateful for it before you sleep.',
    'Lost your rock? No stress — find a new one tomorrow. For tonight, hold anything small and solid, think of the best thing that happened today, and feel grateful. The practice is the point.',
    'Tonight\'s one job: find your rock, hold it, and remember the best thing about today. Thirty seconds. That\'s it. You\'ve got this.',
    'Some nights it\'s profound. Some nights it\'s just "I had a really good coffee." Both count. Hold your rock, find your best moment, feel grateful for it. Sleep well.',
    'The rock doesn\'t care what mood you\'re in. Pick it up anyway. Think of today\'s single best thing. Let the gratitude be real, even if it\'s small.',
    'End the day the way you started this journey — with one good thing. Hold your rock, find that moment from today, feel it completely.',
  ]
  return {
    title: 'The Magic Rock',
    practice: 'Tonight\'s Ritual',
    full: reminders[(day - 3) % reminders.length],
    quote: null,
  }
}

const getDayRitual = (city, day) => {
  const days = DAYS?.[city]
  const idx = Math.max(0, day - 1)
  return days?.[idx] || null
}

export default function RitualScreen({ day, city, cityColor, onComplete }) {
  const color = cityColor || '#EE352E'
  const showMagicRock = day >= 2

  // step 0 = day-specific ritual, step 1 = magic rock
  const [step, setStep] = useState(0)

  const dayRitual = getDayRitual(city, day)
  const magicRockRitual = getMagicRockRitual(city, day)

  const isMagicRockStep = showMagicRock && step === 1
  const ritual = isMagicRockStep ? magicRockRitual : dayRitual

  const handleContinue = () => {
    if (showMagicRock && step === 0) {
      setStep(1)
    } else {
      onComplete()
    }
  }

  const isLastStep = !showMagicRock || step === 1

  if (!ritual) return null

  const illustrationType = isMagicRockStep ? 'magic-rock' : 'city'
  const illustrationBg = isMagicRockStep ? '#111111'
    : city === 'paris' ? '#f4efe5'
    : city === 'london' ? '#e8e0d2'
    : '#0a0a1a'

  return (
    <div style={styles.screen}>
      <div style={styles.card}>
      {/* Illustration */}
      <div style={{ ...styles.illustrationWrap, background: illustrationBg }}>
        <PlaceholderIllustration color={color} type={illustrationType} city={city} />
      </div>

      {/* Content */}
      <div style={styles.content}>
        {/* Step pill for multi-step */}
        {showMagicRock && (
          <div style={styles.stepPill}>
            <div style={{ ...styles.stepDot, background: step === 0 ? color : 'var(--border2)' }} />
            <div style={{ ...styles.stepDot, background: step === 1 ? color : 'var(--border2)' }} />
          </div>
        )}

        {/* Practice label */}
        <p style={styles.practiceLabel}>{ritual.practice || 'Tonight\'s Practice'}</p>

        {/* Title */}
        <h1 style={styles.title}>{ritual.title}</h1>

        {/* Body */}
        <p style={styles.body}>{(day === 2 && !isMagicRockStep) ? ritual.short : (ritual.full || ritual.short)}</p>

        {/* Quote */}
        {ritual.quote && (
          <p style={styles.quote}>"{ritual.quote}"</p>
        )}
      </div>

      {/* Continue button */}
      <div style={styles.footer}>
        <button
          style={{ ...styles.btn, background: color }}
          onClick={handleContinue}
        >
          {isLastStep ? 'Done' : 'Next Ritual →'}
        </button>
      </div>
      </div>
    </div>
  )
}

const styles = {
  screen: {
    position: 'fixed',
    inset: 0,
    background: 'var(--bg)',
    display: 'flex',
    justifyContent: 'center',
    zIndex: 1000,
    overflow: 'hidden',
  },
  card: {
    width: '100%',
    maxWidth: 480,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  illustrationWrap: {
    width: '100%',
    height: '45vh',
    flexShrink: 0,
    overflow: 'hidden',
    background: '#0a0a1a',
  },
  content: {
    flex: 1,
    padding: '20px 24px 8px',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    overflow: 'hidden',
  },
  stepPill: {
    display: 'flex',
    gap: '6px',
    alignItems: 'center',
    marginBottom: '4px',
  },
  stepDot: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    transition: 'background 0.3s ease',
  },
  practiceLabel: {
    fontSize: '0.65rem',
    fontWeight: 700,
    letterSpacing: '2px',
    textTransform: 'uppercase',
    color: 'var(--text3)',
  },
  title: {
    fontSize: '1.7rem',
    fontWeight: 900,
    lineHeight: 1.1,
    color: 'var(--text)',
    fontFamily: 'var(--fn)',
  },
  body: {
    fontSize: '0.88rem',
    lineHeight: 1.6,
    color: 'var(--text2)',
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitLineClamp: 6,
    WebkitBoxOrient: 'vertical',
  },
  quote: {
    fontSize: '0.75rem',
    fontStyle: 'italic',
    color: 'var(--text3)',
    flexShrink: 0,
  },
  footer: {
    padding: '12px 24px 28px',
    flexShrink: 0,
  },
  btn: {
    width: '100%',
    padding: '15px',
    border: 'none',
    borderRadius: '12px',
    fontSize: '1rem',
    fontWeight: 700,
    color: '#fff',
    cursor: 'pointer',
    letterSpacing: '0.5px',
  },
}
